import { Link } from 'react-router-dom';
import { Database, EnvelopeSimple } from '@phosphor-icons/react';

/**
 * Shared footer for MigrationCrunch landing surfaces (/migrate, /migrate/compare,
 * /migrate/:pair). Keeps every public page anchored with consistent legal +
 * contact links. Intentionally social-link-free until accounts exist —
 * adding empty/dead social icons just signals "early product".
 */
export default function MigrationCrunchFooter() {
  return (
    <footer className="border-t border-white/10 bg-[#0A0A12] text-white/70" data-testid="mc-footer">
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-10">
          {/* Brand + tagline */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/migrate" className="flex items-center gap-2 mb-3 hover:opacity-80 transition-opacity">
              <Database size={24} weight="fill" className="text-[#F39C12]" />
              <span className="text-lg font-black text-white">
                Migration<span className="text-[#F39C12]">Crunch</span>
              </span>
            </Link>
            <p className="text-xs text-white/55 leading-relaxed">
              Move every contact, every note, every relationship — without losing a single thread.
            </p>
            <p className="text-[10px] text-white/35 mt-3 uppercase tracking-widest">
              Patent pending · AI Note Re-Association
            </p>
          </div>

          {/* Product */}
          <div>
            <h3 className="text-[10px] font-black text-white uppercase tracking-widest mb-3">
              Product
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/migrate/walkthrough" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-how">
                  How it works
                </Link>
              </li>
              <li>
                <Link to="/migrate/compare" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-compare">
                  Compare vendors
                </Link>
              </li>
              <li>
                <Link to="/migrate/salesforce-true-cost" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-sf-cost">
                  True cost of Salesforce
                </Link>
              </li>
              <li>
                <Link to="/migrate/gemini" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-gemini">
                  What Google Gemini said
                </Link>
              </li>
              <li>
                <Link to="/migrate#pricing" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-pricing">
                  Pricing
                </Link>
              </li>
              <li>
                <Link to="/migrate#fit-check" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-fit">
                  Fit check
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="text-[10px] font-black text-white uppercase tracking-widest mb-3">
              Resources
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <a href="/" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-crm">
                  SalesCrunchCRM
                </a>
              </li>
              <li>
                <Link to="/affiliates" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-affiliate">
                  Affiliate program
                </Link>
              </li>
              <li>
                <Link to="/migrate/act-to-salesforce" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-sample-guide">
                  Sample migration guide
                </Link>
              </li>
              <li>
                <Link to="/migrate/recover-license" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-recover-license">
                  Lost your license key?
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal & contact */}
          <div>
            <h3 className="text-[10px] font-black text-white uppercase tracking-widest mb-3">
              Company
            </h3>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/contact-us" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-contact">
                  Contact us
                </Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-privacy">
                  Privacy policy
                </Link>
              </li>
              <li>
                <Link to="/terms" className="hover:text-amber-300 transition-colors" data-testid="mc-footer-terms">
                  Terms of service
                </Link>
              </li>
              <li>
                <a
                  href="mailto:chip@migrationcrunch.com"
                  className="hover:text-amber-300 transition-colors inline-flex items-center gap-1.5"
                  data-testid="mc-footer-email"
                >
                  <EnvelopeSimple size={13} weight="fill" />
                  chip@migrationcrunch.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-white/5 pt-6 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-[11px] text-white/40">
          <p>
            © {new Date().getFullYear()} MigrationCrunch. Made with care for ACT! refugees.
          </p>
          <p>
            30-day money-back guarantee on AI note recovery · One-time pricing · No subscriptions
          </p>
        </div>
      </div>
    </footer>
  );
}
